// Renders the configured-door SVG to a PNG data URL for the basket/quote
// (the PNG is embedded in emails, where SVG is not supported).
//
// The SVG is drawn straight onto a canvas: its <image> hrefs are inlined as
// data URLs so the serialised SVG is self-contained, then the browser decodes
// it as an image. This replaced html-to-image, which walked the DOM and
// embedded every web font and intermittently never resolved on iOS Safari.

// 1.5x keeps the PDF/email image sharp; phones report 3x, which made the POST >1MB.
const MAX_CAPTURE_PIXEL_RATIO = 1.5;
const CAPTURE_TIMEOUT_MS = 8000;

const XLINK_NS = "http://www.w3.org/1999/xlink";

const dataUrlCache = new Map();

const withTimeout = (promise, ms) =>
    Promise.race([
        promise,
        new Promise((resolve) => window.setTimeout(() => resolve(null), ms)),
    ]);

const getImageHref = (node) =>
    node.getAttribute("href") ||
    node.getAttributeNS(XLINK_NS, "href") ||
    node.getAttribute("xlink:href");

const toDataUrl = (href) => {
    if (!href || href.startsWith("data:")) return Promise.resolve(href);

    const url = new URL(href, window.location.href).toString();
    if (!dataUrlCache.has(url)) {
        dataUrlCache.set(
            url,
            fetch(url)
                .then((res) => (res.ok ? res.blob() : Promise.reject(res.status)))
                .then(
                    (blob) =>
                        new Promise((resolve, reject) => {
                            const reader = new FileReader();
                            reader.onload = () => resolve(reader.result);
                            reader.onerror = reject;
                            reader.readAsDataURL(blob);
                        })
                )
                .catch(() => {
                    dataUrlCache.delete(url);
                    return null;
                })
        );
    }

    return dataUrlCache.get(url);
};

const inlineImages = async (svgEl) => {
    const images = Array.from(svgEl.querySelectorAll("image"));
    await Promise.all(
        images.map(async (node) => {
            const dataUrl = await toDataUrl(getImageHref(node));
            node.removeAttribute("href");
            if (dataUrl) {
                node.setAttributeNS(XLINK_NS, "xlink:href", dataUrl);
            } else {
                node.removeAttributeNS(XLINK_NS, "href");
            }
        })
    );
};

const loadSvgImage = (svgMarkup) =>
    new Promise((resolve, reject) => {
        const image = new Image();
        image.onload = () => resolve(image);
        image.onerror = reject;
        image.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svgMarkup)}`;
    });

const renderDoorImage = async (svgRef, fixedViewBox) => {
    const svgEl = svgRef?.current;
    if (!svgEl) return null;

    try {
        const rect = svgEl.getBoundingClientRect();
        const width = Math.round(rect.width || svgEl.clientWidth);
        const height = Math.round(rect.height || svgEl.clientHeight);
        if (!width || !height) return null;

        const clone = svgEl.cloneNode(true);
        const viewBox = fixedViewBox || svgEl.getAttribute("viewBox");
        if (viewBox) clone.setAttribute("viewBox", viewBox);
        clone.setAttribute("xmlns", "http://www.w3.org/2000/svg");
        clone.setAttribute("xmlns:xlink", XLINK_NS);
        clone.setAttribute("width", width);
        clone.setAttribute("height", height);
        // Text (house number) inherits the page font via CSS, which an SVG
        // image cannot see; give it an explicit family.
        clone.setAttribute("font-family", window.getComputedStyle(svgEl).fontFamily);

        await inlineImages(clone);

        const image = await loadSvgImage(new XMLSerializer().serializeToString(clone));

        const pixelRatio = Math.min(window.devicePixelRatio || 1, MAX_CAPTURE_PIXEL_RATIO);
        const canvas = document.createElement("canvas");
        canvas.width = Math.round(width * pixelRatio);
        canvas.height = Math.round(height * pixelRatio);

        const ctx = canvas.getContext("2d");
        ctx.fillStyle = window.getComputedStyle(svgEl).backgroundColor || "#ffffff";
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(image, 0, 0, canvas.width, canvas.height);

        return canvas.toDataURL("image/png");
    } catch (e) {
        console.error("Door image capture failed", e);
        return null;
    }
};

// The images are optional for the basket/quote, so a render that stalls
// resolves to null instead of blocking the submit.
export const captureConfiguredDoor = (svgRef, fixedViewBox = null) =>
    withTimeout(renderDoorImage(svgRef, fixedViewBox), CAPTURE_TIMEOUT_MS);
